import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdChevronRight } from 'react-icons/md';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

interface PlatformStats {
  views: string;
  reach: string;
  clicks: string;
  engagement: string;
}

interface Campaign {
  id: number;
  title: string;
  budget: string;
  status: string;
  dateRange: string;
  analytics: {
    facebook: string;
    instagram: string;
    email: string;
    tiktok: string;
  };
  detailedStats: {
    facebook: PlatformStats;
    instagram: PlatformStats;
    email: PlatformStats;
    tiktok: PlatformStats;
  };
}

interface PlatformData {
  name: string;
  key: keyof Campaign['detailedStats'];
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const TopTrendingCampaigns: React.FC = () => {
  const { navigate } = useRestaurantNav();
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>('instagram');

  // Top Trending Campaigns Data - empty array to show empty state
const topCampaigns: Campaign[] = [
    {
      id: 1,
      title: 'Summer Fashion Campaign',
      budget: '$3,250.00',
      status: 'Active',
      dateRange: 'Jan 15 - Feb 28, 2024',
      analytics: {
        facebook: '15.20%',
        instagram: '28.50%',
        email: '12.30%',
        tiktok: '18.90%'
      },
      detailedStats: {
        facebook: {
          views: '34',
          reach: '26',
          clicks: '21',
          engagement: '80.9%'
        },
        instagram: {
          views: '56',
          reach: '20',
          clicks: '12',
          engagement: '78.3%'
        },
        email: {
          views: '890',
          reach: '1,234',
          clicks: '234',
          engagement: '5.6%'
        },
        tiktok: {
          views: '134',
          reach: '134',
          clicks: '78',
          engagement: '70.2%'
        }
      }
    }
  ];

  const platforms: PlatformData[] = [
    {
      name: 'Instagram',
      key: 'instagram',
      color: '#E1306C',
      bgColor: 'rgba(225, 48, 108, 0.1)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      name: 'Facebook',
      key: 'facebook',
      color: '#1877F2',
      bgColor: 'rgba(24, 119, 242, 0.1)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    {
      name: 'TikTok',
      key: 'tiktok',
      color: '#FFFFFF',
      bgColor: 'rgba(255, 255, 255, 0.1)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      )
    }
  ];

  const togglePlatform = (platformKey: string) => {
    setExpandedPlatform(expandedPlatform === platformKey ? null : platformKey);
  };

  return (
    <div>
      {/* Header with Title and View More Button */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-medium text-[#EE4423]">Top Trending Campaigns</h3>
        <button 
          onClick={() => navigate('/analytics')}
          className="text-gray-400 text-sm font-medium hover:underline cursor-pointer flex items-center gap-1"
        >
          view more
          <MdChevronRight size={16} />
        </button>
      </div>

      {/* Card with Glassmorphism */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-2xl p-4 border border-gray-700/50 h-[340px] flex flex-col"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: 'linear-gradient(90deg, #212121eb, #373737eb)'
        }}
      >
        {topCampaigns.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <p className="text-gray-400 text-sm mb-4">You haven't scheduled any campaigns yet</p>
              <button 
                onClick={() => navigate('/campaigns')}
                className="px-4 py-2 bg-[#EE4423] hover:bg-[#d63d20] text-white rounded-full text-sm transition-colors"
              >
                Schedule Campaign
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 flex-1 overflow-y-auto">
            {topCampaigns.map((campaign) => (
            <div key={campaign.id} className="bg rounded-xl p-2 border-gray-600/30">
              {/* Campaign Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  {/* <h4 className="text-white font-semibold text-base mb-1">{campaign.title}</h4> */}
                  {/* <p className="text-gray-400 text-[10px]">{campaign.dateRange}</p> */}
                </div>
                {/* <div className="text-right">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    campaign.status === 'Active' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  }`}>
                    {campaign.status}
                  </span>
                </div> */}
              </div>

              {/* Two Column Layout: Image (1) | Analytics (2) */}
              <div className="grid grid-cols-3 gap-3">
                {/* Left Side - Campaign Image */}
                <div className="col-span-1">
                  <div className="rounded-xl overflow-hidden h-full">
                    <img 
                      src="https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1773771536254-flyer_1773771531522_0.png"
                      alt={campaign.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Right Side - Collapsible Platform Analytics */}
                <div className="col-span-2 space-y-2">
                  {platforms.map((platform) => (
                    <div key={platform.key}>
                      {/* Platform Header - Clickable */}
                      <motion.div
                        onClick={() => togglePlatform(platform.key)}
                        className="flex items-center justify-between p-2 rounded-sm cursor-pointer transition-all"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          // border: expandedPlatform === platform.key ? `1px solid ${platform.color}` : '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-center gap-3 cursor-pointer">
                          {/* Platform Icon */}
                          <div 
                            className="w-6 h-6 rounded-lg flex items-center justify-center"
                            style={{ 
                              backgroundColor: platform.bgColor,
                              color: platform.color
                            }}
                          >
                            {platform.icon}
                          </div>
                          
                          {/* Platform Name and Percentage */}
                          <div>
                            <div className="text-white text-sm font-medium text-base">{platform.name}</div>
                            {/* <div className="text-gray-400 text-xs">Ir 2</div> */}
                          </div>
                        </div>

                        {/* Chevron Icon */}
                        <motion.div
                          animate={{ rotate: expandedPlatform === platform.key ? 180 : 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.div>
                      </motion.div>

                      {/* Collapsible Stats Section */}
                      <AnimatePresence>
                        {expandedPlatform === platform.key && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ 
                              height: 'auto', 
                              opacity: 1,
                              transition: {
                                height: {
                                  duration: 0.4,
                                  ease: [0.04, 0.62, 0.23, 0.98]
                                },
                                opacity: {
                                  duration: 0.3,
                                  delay: 0.1
                                }
                              }
                            }}
                            exit={{ 
                              height: 0, 
                              opacity: 0,
                              transition: {
                                height: {
                                  duration: 0.3,
                                  ease: [0.04, 0.62, 0.23, 0.98]
                                },
                                opacity: {
                                  duration: 0.2
                                }
                              }
                            }}
                            className="overflow-hidden"
                          >
                            <div className="mt-2 p-4 rounded-xl" style={{
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: `1px solid ${platform.color}40`
                            }}>
                              {/* Stats Grid */}
                              <div className="grid grid-cols-4 gap-3">
                                <div className="text-ceter">
                                  <div className="text-sm font-medium text-white mb-1">
                                    {campaign.detailedStats[platform.key].views}
                                  </div>
                                  <div className="text-[10px] text-gray-400">views</div>
                                </div>
                                <div className="text-cener">
                                  <div className="text-sm font-medium text-white mb-1">
                                    {campaign.detailedStats[platform.key].reach}
                                  </div>
                                  <div className="text-[10px] text-gray-400">Reached</div>
                                </div>
                                <div className="text-cener">
                                  <div className="text-sm font-medium text-white mb-1">
                                    {campaign.detailedStats[platform.key].engagement}
                                  </div>
                                  <div className="text-[10px] text-gray-400">engagement</div>
                                </div>
                                <div className="text-ceter">
                                  <div className="text-sm font-medium text-white mb-1">
                                    {campaign.detailedStats[platform.key].clicks}
                                  </div>
                                  <div className="text-[10px] text-gray-400">clicks</div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TopTrendingCampaigns;
